import * as IF from "infrontjs";
import template from './template.html';

class ContentHeader extends HTMLElement
{
    static get observedAttributes() {
        return ["title", "breadcrumb"];
    }


    constructor()
    {
        super();
        // interne State-Defaults
        this._title = this.getAttribute("title") ?? "";
        this._breadcrumb = this._parseBreadcrumbAttr(
            this.getAttribute("breadcrumb")
        );
        this._isConnected = false;
        this._typewriterActive = false;
        this._currentDisplayTitle = "";
        this._pendingTitle = null;
    }

    /* ---------- Public API (Properties, optional) ---------- */
    get title() {
        return this._title;
    }
    set title(val) {
        if (val == null) this.removeAttribute("title");
        else this.setAttribute("title", String(val));
    }

    get breadcrumb() {
        return [...this._breadcrumb];
    }
    set breadcrumb(val) {
        // Erlaubt: Array oder String (JSON/Komma)
        if (Array.isArray(val)) {
            this.setAttribute("breadcrumb", JSON.stringify(val));
        } else if (typeof val === "string") {
            this.setAttribute("breadcrumb", val);
        } else if (val == null) {
            this.removeAttribute("breadcrumb");
        } else {
            console.warn("breadcrumb expects Array or String");
        }
    }


    connectedCallback()
    {
        if ( false === this._isConnected )
        {
            this.app = IF.App.get();
            this.render();
            this.init();

            this._isConnected = true;
        }
    }

    _parseBreadcrumbAttr(val) {
        if (!val) return [];
        // Versuche JSON zuerst
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed)) {
                return parsed.map((x) => String(x));
            }
        } catch (_) {
            // ignorieren – fällt auf Komma-Liste zurück
        }
        // Fallback: Komma- oder ">"-getrennt
        return String(val)
            .split(/,|>/)
            .map((s) => s.trim())
            .filter(Boolean);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return;

        if (name === "title") {
            const previousTitle = this._title;
            this._title = newValue ?? "";
            if (this._isConnected && previousTitle !== this._title) {
                if (this._typewriterActive) {
                    this._pendingTitle = this._title;
                } else {
                    this._animateTypewriter(this._title);
                }
                return;
            }
        }
        if (name === "breadcrumb") {
            this._breadcrumb = this._parseBreadcrumbAttr(newValue);
            if (this._isConnected) {
                const breadcrumbContainer = this.querySelector('.breadcrumb');
                if (breadcrumbContainer) {
                    const itemsHtml = this._breadcrumb.map((itemText, index) => {
                        const isLast = index === this._breadcrumb.length - 1;
                        const activeClass = isLast ? ' active' : '';
                        const ariaCurrent = isLast ? ' aria-current="page"' : '';
                        return `<li class="breadcrumb-item${activeClass}"${ariaCurrent}>${itemText}</li>`;
                    }).join('');
                    breadcrumbContainer.innerHTML = itemsHtml;
                    this._animateBreadcrumb();
                }
            }
            return;
        }

        // sofort neu zeichnen, wenn im DOM
        if ( this._isConnected === true ) {
            this.render();
        }
    }

    init()
    {
    }

    async _animateBreadcrumb() {
        const breadcrumbItems = this.querySelectorAll('.breadcrumb-item');
        if (!breadcrumbItems.length) return;

        breadcrumbItems.forEach((item, index) => {
            item.classList.add('fade-in-element');
            item.style.animationDelay = `${index * 100}ms`; // Staggered delay
        });
    }

    async _animateTypewriter(newTitle) {
        if (this._typewriterActive) return;
        this._typewriterActive = true;
        this._pendingTitle = null;

        const titleElement = this.querySelector('.typewriter-title');
        if (!titleElement) {
            this.render();
            this._typewriterActive = false;
            return;
        }

        // Clear current content and create container for characters
        titleElement.innerHTML = '';
        this._currentDisplayTitle = "";

        // Type out each character with fade-in effect
        for (let i = 0; i < newTitle.length; i++) {
            // Check if we need to interrupt for a new title
            if (this._pendingTitle !== null) {
                break;
            }
            
            const char = newTitle[i];
            const span = document.createElement('span');
            span.textContent = char;
            span.classList.add('fade-in-element'); // Use new class
            span.style.animationDelay = '0ms';
            
            titleElement.appendChild(span);
            this._currentDisplayTitle += char;
            
            await new Promise(resolve => setTimeout(resolve, 30));
        }

        this._typewriterActive = false;

        // If there's a pending title, start animating it
        if (this._pendingTitle !== null) {
            const nextTitle = this._pendingTitle;
            this._pendingTitle = null;
            await this._animateTypewriter(nextTitle);
        }
    }

    render()
    {
        if ( this.app.view ) {
            this.innerHTML = this.app.view.getHtml(
                template,
                {
                    title: this._currentDisplayTitle || this._title,
                    breadcrumb: this._breadcrumb
                }
            );
        }
        // Initialize typewriter for first render
        if (this._isConnected && !this._typewriterActive && this._title) {
            setTimeout(() => {
                this._animateTypewriter(this._title);
                this._animateBreadcrumb();
            }, 100);
        }
    }
}

customElements.define( 'content-header', ContentHeader );